/**
 * IELTS V2 Phase 1C — Full Mock Exam pages.
 *
 * Three pages exported:
 *   IeltsMockHubPage     /resources/ielts/mock
 *   IeltsMockSessionPage /resources/ielts/mock/session/:mockId
 *   IeltsMockResultsPage /resources/ielts/mock/results/:mockId
 *
 * The Mock is a coordinator: it reuses the existing Listening / Reading /
 * Writing / Speaking engines. When entering a section from the Mock
 * Session, `localStorage['ielts_mock_context']` is set. The existing skill
 * simulation pages read this and, on submission, navigate back to the
 * Mock Session (rather than exposing individual results) — enforcing
 * "no answer leakage during an active mock".
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, PlayCircle, ChevronRight, TrophyIcon, ShieldCheck, Headphones, BookOpen, PenSquare, Mic, ArrowLeft, RotateCcw } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });

const SECTION_META = {
  listening:   { label: 'Listening',      icon: <Headphones className="h-4 w-4" />, hint: '4 parts · 40 questions · ~32 minutes' },
  reading:     { label: 'Reading',        icon: <BookOpen  className="h-4 w-4" />, hint: '3 sections · 40 questions · 60 minutes' },
  writing_t1:  { label: 'Writing · Task 1', icon: <PenSquare className="h-4 w-4" />, hint: 'Minimum 150 words · 20 minutes' },
  writing_t2:  { label: 'Writing · Task 2', icon: <PenSquare className="h-4 w-4" />, hint: 'Minimum 250 words · 40 minutes' },
  speaking:    { label: 'Speaking',       icon: <Mic       className="h-4 w-4" />, hint: '3 parts · ~14 minutes' },
};
const SECTION_ORDER = ['listening', 'reading', 'writing_t1', 'writing_t2', 'speaking'];

const setMockContext = (mockId, section) =>
  localStorage.setItem('ielts_mock_context',
    JSON.stringify({ mockId, section, at: Date.now() }));
const clearMockContext = () =>
  localStorage.removeItem('ielts_mock_context');

// ---------------------------------------------------------------------------
// Mock Hub — variant picker
// ---------------------------------------------------------------------------
export function IeltsMockHubPage() {
  const nav = useNavigate();
  const [variants, setVariants] = useState(null);
  const [history, setHistory] = useState([]);
  const [starting, setStarting] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [v, h] = await Promise.all([
          ax.get('/api/ielts/v2/mock/variants'),
          ax.get('/api/ielts/v2/mock/history').catch(() => ({ data: { items: [] } })),
        ]);
        setVariants(v.data.variants);
        setHistory(h.data.items || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, []);

  const start = async (variant_id) => {
    setStarting(variant_id);
    try {
      const r = await ax.post('/api/ielts/v2/mock/start', { variant_id });
      clearMockContext();
      nav(`/resources/ielts/mock/session/${r.data.id}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setStarting(null);
    }
  };

  const grouped = {
    academic: (variants || []).filter((v) => v.test_variant === 'academic'),
    general_training: (variants || []).filter((v) => v.test_variant === 'general_training'),
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ielts-mock-hub">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
        <button onClick={() => nav('/resources/ielts')}
          data-testid="mock-hub-back"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-4">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to IELTS Hub
        </button>
        <div className="text-[11px] uppercase tracking-widest text-indigo-600 font-bold">Winhouse · IELTS V2 · Full Mock</div>
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mt-1">Full IELTS Mock Exam</h1>
        <p className="text-slate-600 mt-2 max-w-3xl text-sm">
          A complete simulated IELTS experience across Listening, Reading, Writing and Speaking. Stricter than practice — no answers, transcripts or feedback are revealed while the mock is active. Once all four sections are submitted, you receive a full Winhouse Estimated IELTS Practice Band report.
        </p>

        {err && (
          <div className="mt-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm p-3" data-testid="mock-hub-error">{err}</div>
        )}

        {!variants && <div className="mt-8 text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading mock variants…</div>}

        {variants && (
          <>
            {['academic', 'general_training'].map((v) => (
              <div key={v} className="mt-10">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold flex items-center gap-2">
                  {v === 'academic' ? 'IELTS Academic' : 'IELTS General Training'}
                  <span className="text-slate-400">· {grouped[v].length} variants</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                  {grouped[v].map((mv) => (
                    <div key={mv.id}
                      data-testid={`mock-variant-${mv.id}`}
                      className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-indigo-600 font-bold">{mv.subtitle}</div>
                        <div className="text-lg font-bold text-slate-900 mt-1">{mv.title}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          Nominal duration ~{mv.total_duration_minutes} min · L 40Q · R 40Q · W Task 1+2 · Speaking 3 parts
                        </div>
                      </div>
                      <div className="text-xs text-slate-600 leading-relaxed">{mv.description}</div>
                      <button onClick={() => start(mv.id)}
                        disabled={!!starting}
                        data-testid={`mock-start-${mv.id}`}
                        className="mt-auto inline-flex items-center justify-center gap-1 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold px-4 py-2 disabled:opacity-60">
                        {starting === mv.id
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <><PlayCircle className="h-4 w-4" /> Start Full Mock</>}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* History */}
            <div className="mt-12">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Your Mock Attempts</div>
              {history.length === 0
                ? <div className="text-sm text-slate-500 mt-2" data-testid="mock-history-empty">No mocks attempted yet.</div>
                : (
                  <div className="mt-3 divide-y divide-slate-200 rounded-2xl bg-white border border-slate-200" data-testid="mock-history-list">
                    {history.map((h) => (
                      <div key={h.id} className="flex flex-wrap items-center gap-3 p-4">
                        <div className="flex-1 min-w-[200px]">
                          <div className="text-sm font-semibold text-slate-900">{h.title}</div>
                          <div className="text-[11px] text-slate-500">
                            {new Date(h.started_at).toLocaleString()} · {h.status === 'submitted' ? 'Completed' : 'In progress'}
                          </div>
                        </div>
                        {h.overall_band != null && (
                          <div className="text-right">
                            <div className="text-[10px] text-slate-500 uppercase tracking-widest">Overall</div>
                            <div className="text-xl font-bold text-slate-900">{h.overall_band.toFixed(1)}</div>
                          </div>
                        )}
                        {h.status === 'submitted'
                          ? <button onClick={() => nav(`/resources/ielts/mock/results/${h.id}`)}
                              className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5"
                              data-testid={`mock-view-${h.id}`}>View Results</button>
                          : <button onClick={() => nav(`/resources/ielts/mock/session/${h.id}`)}
                              className="rounded-full bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-3 py-1.5"
                              data-testid={`mock-resume-${h.id}`}>Resume Mock</button>}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mock Session — section navigator
// ---------------------------------------------------------------------------
export function IeltsMockSessionPage() {
  const { mockId } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await ax.get(`/api/ielts/v2/mock/state/${mockId}`);
      setState(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, [mockId]);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh when tab regains focus (student returns from a section)
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const startSection = async (section) => {
    if (!state) return;
    setBusy(true);
    try {
      let childId;
      let route;
      const refs = state.content_refs || {};
      if (section === 'listening') {
        const r = await ax.post('/api/ielts/v2/listening/simulation/start', { variant: state.test_variant });
        childId = r.data.id;
        route = '/resources/ielts/listening/simulation';
      } else if (section === 'reading') {
        const r = await ax.post('/api/ielts/v2/reading/simulation/start', { variant: refs.reading_variant || state.test_variant });
        childId = r.data.id;
        route = '/resources/ielts/reading/simulation';
      } else if (section === 'writing_t1' || section === 'writing_t2') {
        const task_id = section === 'writing_t1' ? refs.writing_t1_id : refs.writing_t2_id;
        const r = await ax.post('/api/ielts/v2/writing/attempts/start', { task_id });
        childId = r.data.id;
        route = `/resources/ielts/writing/workspace/${childId}`;
      } else if (section === 'speaking') {
        const r = await ax.post('/api/ielts/v2/speaking/attempts/start', { mode: 'full' });
        childId = r.data.id;
        route = `/resources/ielts/speaking/session/${childId}`;
      }
      await ax.post(`/api/ielts/v2/mock/${mockId}/link`, { section, child_attempt_id: childId });
      setMockContext(mockId, section);
      nav(route);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!window.confirm('Submit the full mock? This will compute your overall Winhouse Estimated IELTS Practice Band and cannot be changed.')) return;
    setBusy(true);
    try {
      await ax.post(`/api/ielts/v2/mock/${mockId}/submit`);
      clearMockContext();
      nav(`/resources/ielts/mock/results/${mockId}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  if (err) return <div className="p-6 max-w-2xl mx-auto"><div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm p-4" data-testid="mock-session-error">{err}</div></div>;
  if (!state) return <div className="p-6 text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading mock…</div>;

  const secs = state.sections || {};
  const allSubmitted = SECTION_ORDER.every((s) => secs[s]?.status === 'submitted' || secs[s]?.status === 'auto_scored');
  const submitted = state.status === 'submitted';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="ielts-mock-session">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-6">
        <button onClick={() => nav('/resources/ielts/mock')}
          data-testid="mock-session-back"
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Mock Hub
        </button>
        <div>
          <div className="text-xs uppercase tracking-widest text-indigo-400 font-bold">{state.test_variant === 'academic' ? 'IELTS Academic' : 'IELTS General Training'} · Full Mock</div>
          <h1 className="text-3xl md:text-4xl font-bold mt-1">{state.title}</h1>
          <p className="text-slate-400 text-sm mt-1">Complete each section in order. Answers and feedback are hidden until the full mock is submitted.</p>
        </div>

        {/* Non-affiliation banner */}
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-emerald-400 flex-none mt-0.5" />
          <div className="text-xs text-slate-300">
            <div className="font-semibold text-slate-100">Winhouse Estimated IELTS Practice Band</div>
            This is a Winhouse practice mock. Not affiliated with IDP, British Council or Cambridge Assessment English. Final band is estimated after all four skills are submitted.
          </div>
        </div>

        {/* Section grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SECTION_ORDER.map((s) => {
            const st = secs[s]?.status || 'not_started';
            const done = st === 'submitted' || st === 'auto_scored';
            const inProg = st === 'in_progress';
            return (
              <div key={s} data-testid={`mock-section-${s}`}
                className={`rounded-2xl border p-4 flex items-center gap-4 ${done ? 'bg-emerald-950/40 border-emerald-800' : inProg ? 'bg-indigo-950/50 border-indigo-800' : 'bg-slate-900 border-slate-800'}`}>
                <div className="text-slate-200">{SECTION_META[s].icon}</div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-100">{SECTION_META[s].label}</div>
                  <div className="text-[11px] text-slate-400">{SECTION_META[s].hint}</div>
                  <div className={`text-[11px] mt-1 font-semibold ${done ? 'text-emerald-400' : inProg ? 'text-indigo-300' : 'text-slate-500'}`}>
                    {done ? 'Submitted' : inProg ? 'In progress' : 'Not started'}
                  </div>
                </div>
                {!submitted && (
                  <button onClick={() => startSection(s)}
                    disabled={busy || done}
                    data-testid={`mock-start-section-${s}`}
                    className={`inline-flex items-center gap-1 rounded-full text-xs font-bold px-3 py-1.5 disabled:opacity-40 ${done ? 'bg-emerald-500 text-black' : 'bg-white text-slate-900 hover:bg-slate-100'}`}>
                    {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    {done ? 'Done' : inProg ? 'Resume' : 'Start'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Submit / Results */}
        {submitted
          ? <button onClick={() => nav(`/resources/ielts/mock/results/${mockId}`)}
              data-testid="mock-view-results"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-5 py-3">
              <TrophyIcon className="h-5 w-5" /> View Full Mock Results
            </button>
          : <button onClick={submit}
              disabled={!allSubmitted || busy}
              data-testid="mock-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white font-bold px-5 py-3 disabled:opacity-40 disabled:cursor-not-allowed">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <TrophyIcon className="h-5 w-5" />}
              {allSubmitted ? 'Finalize Mock & See Overall Band' : `Complete all 5 sections first (${SECTION_ORDER.filter((s) => secs[s]?.status === 'submitted' || secs[s]?.status === 'auto_scored').length}/5)`}
            </button>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mock Results Centre — aggregated
// ---------------------------------------------------------------------------
export function IeltsMockResultsPage() {
  const { mockId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await ax.get(`/api/ielts/v2/mock/results/${mockId}`);
        setData(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, [mockId]);

  if (err) return <div className="p-6 max-w-2xl mx-auto"><div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm p-4">{err}</div></div>;
  if (!data) return <div className="p-6 text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading results…</div>;

  const r = data.results;
  const skills = r.skills;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ielts-mock-results">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-10 space-y-8">
        <button onClick={() => nav('/resources/ielts/mock')}
          data-testid="mock-results-back"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Mock Hub
        </button>

        {/* Overall band headline */}
        <div className="rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 text-white p-8 md:p-10">
          <div className="text-[11px] uppercase tracking-widest opacity-80 font-bold">Winhouse · IELTS V2 · Full Mock</div>
          <div className="text-2xl md:text-3xl font-bold mt-1">{data.title}</div>
          <div className="mt-6 flex flex-wrap items-end gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-widest opacity-80">{r.label}</div>
              <div className="text-6xl md:text-7xl font-black mt-1" data-testid="mock-overall-band">{r.overall_band.toFixed(1)}</div>
            </div>
            <div className="text-sm max-w-xl opacity-90">{r.disclaimer}</div>
          </div>
        </div>

        {/* Per-skill bands */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['listening', 'reading', 'writing', 'speaking'].map((s) => (
            <div key={s} data-testid={`mock-skill-${s}`}
              className={`rounded-2xl bg-white border p-5 ${r.strongest_skill === s ? 'border-emerald-400 shadow-emerald-100 shadow' : r.weakest_skill === s ? 'border-amber-400 shadow-amber-100 shadow' : 'border-slate-200'}`}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
                {SECTION_META[s === 'writing' ? 'writing_t1' : s].icon}
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </div>
              <div className="text-4xl font-bold text-slate-900 mt-2">{skills[s].toFixed(1)}</div>
              {r.strongest_skill === s && <div className="text-[10px] text-emerald-700 font-bold mt-1">STRONGEST</div>}
              {r.weakest_skill === s && <div className="text-[10px] text-amber-700 font-bold mt-1">FOCUS AREA</div>}
            </div>
          ))}
        </div>

        {/* Recommendation */}
        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Recommended Next Practice</div>
          <div className="text-sm text-slate-800 mt-2">{r.recommended_next_practice}</div>
        </div>

        {/* Per-child deep links */}
        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Section Reports</div>
          <div className="space-y-2 text-sm" data-testid="mock-section-reports">
            {Object.entries(r.per_child || {}).map(([sec, info]) => {
              const links = {
                listening: `/resources/ielts/results/${info.child_attempt_id}`,
                reading: `/resources/ielts/reading/results/${info.child_attempt_id}`,
                writing_t1: `/resources/ielts/writing/results/${info.child_attempt_id}`,
                writing_t2: `/resources/ielts/writing/results/${info.child_attempt_id}`,
                speaking: `/resources/ielts/speaking/results/${info.child_attempt_id}`,
              };
              return (
                <div key={sec} className="flex items-center gap-3">
                  <span className="text-slate-600">{SECTION_META[sec].label}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-800 font-semibold">Band {info.band?.toFixed(1)}</span>
                  <span className="ml-auto">
                    <a href={links[sec]}
                      data-testid={`mock-section-link-${sec}`}
                      className="text-indigo-600 hover:text-indigo-800 text-xs font-semibold">Open detailed report →</a>
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button onClick={() => nav('/resources/ielts/mock')}
            data-testid="mock-retake"
            className="inline-flex items-center gap-1 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold px-4 py-2">
            <RotateCcw className="h-4 w-4" /> Take Another Mock
          </button>
          <button onClick={() => nav('/student/dashboard')}
            className="inline-flex items-center gap-1 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold px-4 py-2">
            Back to Student Portal
          </button>
        </div>
      </div>
    </div>
  );
}
